import React, { useEffect, useMemo, useState, useCallback, useRef } from 'react';
import { createClient } from '@supabase/supabase-js';
import { IconPlus, IconSearch, IconEdit, IconTrash } from './Icons';
import ExcelJS from 'exceljs';
import './Departments.css';

const supabaseUrl = process.env.REACT_APP_SUPABASE_URL || '';
const supabaseAnonKey = process.env.REACT_APP_SUPABASE_ANON_KEY || '';
const noOpLock = async (_name, _acquireTimeout, fn) => fn();
const contactsSupabase = createClient(supabaseUrl, supabaseAnonKey, {
  auth: { persistSession: true, autoRefreshToken: true, detectSessionInUrl: true, lock: noOpLock },
});

const FETCH_TIMEOUT_MS = 12000;

const CONTACT_COLUMNS = [
  { key: 'full_name', label: 'Full Name' },
  { key: 'email', label: 'Email' },
  { key: 'mobile_phone', label: 'Mobile No.' },
  { key: 'telephone', label: 'Tel No.' },
  { key: 'organization_name', label: 'Organization' },
  { key: 'job_title', label: 'Position' },
  { key: 'country', label: 'Country' },
  { key: 'notes', label: 'Notes' },
];

const emptyContact = () => ({
  full_name: '',
  email: '',
  mobile_phone: '',
  telephone: '',
  organization_name: '',
  job_title: '',
  country: '',
  notes: '',
});

export default function Contacts() {
  const [contacts, setContacts] = useState([]);
  const [search, setSearch] = useState('');
  const [showAddModal, setShowAddModal] = useState(false);
  const [form, setForm] = useState(emptyContact());
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [editingContact, setEditingContact] = useState(null);
  const mountedRef = useRef(true);

  const runWakeUp = useCallback(() => {
    return Promise.race([
      contactsSupabase.from('profiles').select('id').limit(1),
      new Promise((_, reject) => setTimeout(() => reject(new Error('wakeup')), 5000)),
    ]).catch(() => {});
  }, []);

  const fetchContacts = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const fetchPromise = contactsSupabase
        .from('contacts')
        .select('*')
        .order('full_name', { ascending: true, nullsFirst: false });
      const timeoutPromise = new Promise((_, reject) =>
        setTimeout(() => reject(new Error('Request timed out. Try refreshing.')), FETCH_TIMEOUT_MS)
      );
      const result = await Promise.race([fetchPromise, timeoutPromise]);
      const { data, error } = result;
      if (error) {
        setError(error.message);
        setContacts([]);
      } else {
        setContacts(data || []);
      }
    } catch (e) {
      setError(e?.message || 'Failed to load contacts.');
      setContacts([]);
    } finally {
      setLoading(false);
    }
  }, []);

  const refreshSessionAndFetch = useCallback(async () => {
    mountedRef.current = true;
    setError(null);
    try {
      await contactsSupabase.auth.getSession();
      await runWakeUp();
      await fetchContacts();
    } catch (e) {
      setError(e?.message || 'Failed to load');
    }
  }, [runWakeUp, fetchContacts]);

  useEffect(() => {
    let cancelled = false;
    mountedRef.current = true;
    (async () => {
      const { error: sessionErr } = await contactsSupabase.auth.getSession();
      if (cancelled) return;
      if (sessionErr) {
        setError(sessionErr.message);
        setLoading(false);
        return;
      }
      await runWakeUp();
      if (cancelled) return;
      await fetchContacts();
    })();
    return () => {
      cancelled = true;
      mountedRef.current = false;
    };
  }, [runWakeUp, fetchContacts]);

  useEffect(() => {
    const onVisible = () => {
      if (document.visibilityState !== 'visible') return;
      refreshSessionAndFetch();
    };
    document.addEventListener('visibilitychange', onVisible);
    return () => document.removeEventListener('visibilitychange', onVisible);
  }, [refreshSessionAndFetch]);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return contacts;
    return contacts.filter((c) => {
      return (
        (c.full_name || '').toLowerCase().includes(q) ||
        (c.email || '').toLowerCase().includes(q) ||
        (c.organization_name || '').toLowerCase().includes(q) ||
        (c.country || '').toLowerCase().includes(q)
      );
    });
  }, [contacts, search]);

  const handleOpenAdd = () => {
    setEditingContact(null);
    setForm(emptyContact());
    setShowAddModal(true);
  };

  const handleOpenEdit = (contact) => {
    setEditingContact(contact);
    setForm({
      full_name: contact.full_name || '',
      email: contact.email || '',
      mobile_phone: contact.mobile_phone || '',
      telephone: contact.telephone || '',
      organization_name: contact.organization_name || '',
      job_title: contact.job_title || '',
      country: contact.country || '',
      notes: contact.notes || '',
    });
    setShowAddModal(true);
  };

  const handleSave = async () => {
    const name = form.full_name.trim();
    if (!name) {
      setShowAddModal(false);
      return;
    }

    const payload = {
      full_name: name,
      email: form.email.trim() || null,
      mobile_phone: form.mobile_phone.trim() || null,
      telephone: form.telephone.trim() || null,
      organization_name: form.organization_name.trim() || null,
      job_title: form.job_title.trim() || null,
      country: form.country.trim() || null,
      notes: form.notes.trim() || null,
    };

    try {
      setSaving(true);
      setError(null);
      await runWakeUp();
      if (editingContact) {
        const { data, error } = await contactsSupabase
          .from('contacts')
          .update(payload)
          .eq('id', editingContact.id)
          .select()
          .single();
        if (error) throw error;
        setContacts((prev) =>
          prev.map((c) => (c.id === editingContact.id ? data : c)),
        );
      } else {
        const { data, error } = await contactsSupabase
          .from('contacts')
          .insert(payload)
          .select()
          .single();
        if (error) throw error;
        setContacts((prev) => [...prev, data]);
      }
      setShowAddModal(false);
      setEditingContact(null);
    } catch (e) {
      console.warn('[Contacts] handleSave error', e);
      setError(e?.message || 'Failed to save contact.');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (contact) => {
    if (!window.confirm(`Delete contact "${contact.full_name}"?`)) return;
    try {
      setSaving(true);
      setError(null);
      await runWakeUp();
      const { error } = await contactsSupabase
        .from('contacts')
        .delete()
        .eq('id', contact.id);
      if (error) throw error;
      setContacts((prev) => prev.filter((c) => c.id !== contact.id));
    } catch (e) {
      console.warn('[Contacts] handleDelete error', e);
      setError(e?.message || 'Failed to delete contact.');
    } finally {
      setSaving(false);
    }
  };

  const handleExportExcel = async () => {
    if (!contacts.length) return;

    const workbook = new ExcelJS.Workbook();
    const sheet = workbook.addWorksheet('Contacts');

    const headerFill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FFB45309' } };
    const headerFont = { color: { argb: 'FFFFFFFF' }, bold: true };
    try {
      const logoUrl = process.env.PUBLIC_URL + '/logo.png';
      const res = await fetch(logoUrl);
      if (res.ok) {
        const blob = await res.blob();
        const arrayBuffer = await blob.arrayBuffer();
        const imageId = workbook.addImage({ buffer: arrayBuffer, extension: 'png' });
        sheet.addImage(imageId, { tl: { col: 0, row: 0 }, ext: { width: 180, height: 48 } });
      }
    } catch (e) {
      console.warn('[Contacts] Excel logo fetch failed', e);
    }
    const titleRowIndex = 4;
    const titleRow = sheet.getRow(titleRowIndex);
    titleRow.getCell(1).value = 'Contacts';
    titleRow.height = 20;
    titleRow.font = { bold: true, size: 14, color: { argb: 'FFB45309' } };
    sheet.mergeCells(titleRowIndex, 1, titleRowIndex, CONTACT_COLUMNS.length + 1);
    const headerRowIndex = titleRowIndex + 2;
    const headerRow = sheet.getRow(headerRowIndex);
    headerRow.getCell(1).value = '';
    CONTACT_COLUMNS.forEach((col, idx) => { headerRow.getCell(idx + 2).value = col.label; });
    headerRow.eachCell((cell) => {
      cell.fill = headerFill;
      cell.font = headerFont;
      cell.alignment = { vertical: 'middle', horizontal: 'left' };
      cell.border = { top: { style: 'thin', color: { argb: 'FFE5E7EB' } }, left: { style: 'thin', color: { argb: 'FFE5E7EB' } }, bottom: { style: 'thin', color: { argb: 'FFE5E7EB' } }, right: { style: 'thin', color: { argb: 'FFE5E7EB' } } };
    });
    let rowIndex = headerRowIndex + 1;
    filtered.forEach((c) => {
      const row = sheet.getRow(rowIndex++);
      row.getCell(1).value = '';
      CONTACT_COLUMNS.forEach((col, idx) => { row.getCell(idx + 2).value = c[col.key] ?? ''; });
      row.eachCell((cell) => {
        cell.border = { top: { style: 'thin', color: { argb: 'FFE5E7EB' } }, left: { style: 'thin', color: { argb: 'FFE5E7EB' } }, bottom: { style: 'thin', color: { argb: 'FFE5E7EB' } }, right: { style: 'thin', color: { argb: 'FFE5E7EB' } } };
      });
    });
    const widths = [6, 24, 26, 18, 26, 26, 16, 32, 40];
    sheet.columns = widths.map((w) => ({ width: w }));
    const buffer = await workbook.xlsx.writeBuffer();
    const blob = new Blob([buffer], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `contacts-${new Date().toISOString().slice(0, 10)}.xlsx`;
    document.body.appendChild(a);
    a.click();
    a.remove();
    URL.revokeObjectURL(url);
  };

  return (
    <div className="departments-page">
      <div className="departments-card">
        <div className="departments-header-row">
          <h1 className="departments-title">Contacts</h1>
          <div className="departments-header-actions">
            <button type="button" className="departments-header-btn departments-header-btn-primary" onClick={handleOpenAdd}>
              <IconPlus /> Add contact
            </button>
          </div>
        </div>
        <div className="departments-toolbar">
          <button type="button" className="departments-toolbar-btn" onClick={handleExportExcel}>Excel</button>
          <div className="departments-search-wrap">
            <IconSearch />
            <input type="search" className="departments-search" placeholder="Search contacts" value={search} onChange={(e) => setSearch(e.target.value)} />
          </div>
        </div>
        {error && <div className="projects-error" role="alert">{error}</div>}
        {loading ? (
          <div className="projects-loading">Loading contacts…</div>
        ) : (
          <div className="departments-table-wrapper">
            <table className="departments-table">
              <thead>
                <tr><th />{CONTACT_COLUMNS.map((col) => <th key={col.key}>{col.label}</th>)}</tr>
              </thead>
              <tbody>
                {filtered.length === 0 ? (
                  <tr><td colSpan={CONTACT_COLUMNS.length + 1} className="departments-empty">No contacts found.</td></tr>
                ) : (
                  filtered.map((c) => (
                    <tr key={c.id}>
                      <td>
                        <div className="departments-row-actions">
                          <button type="button" className="departments-row-icon-btn" onClick={() => handleOpenEdit(c)} aria-label="Edit contact"><IconEdit /></button>
                          <button type="button" className="departments-row-icon-btn" onClick={() => handleDelete(c)} aria-label="Delete contact"><IconTrash /></button>
                        </div>
                      </td>
                      {CONTACT_COLUMNS.map((col) => <td key={col.key}>{c[col.key] ?? '—'}</td>)}
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        )}
      </div>
      {/* Add/Edit modal - same structure as original */}
      {showAddModal && (
        <div className="departments-modal-backdrop" onClick={() => setShowAddModal(false)}>
          <div className="departments-modal" onClick={(e) => e.stopPropagation()}>
            <div className="departments-modal-header">
              <h2 className="departments-modal-title">{editingContact ? 'Edit contact' : 'Add contact'}</h2>
              <button type="button" className="departments-modal-close" onClick={() => setShowAddModal(false)} aria-label="Close">×</button>
            </div>
            <div className="departments-modal-body">
              {CONTACT_COLUMNS.map((col) => (
                <label key={col.key} className="departments-form-label">
                  {col.label}
                  <input
                    className="departments-form-input"
                    type={col.key === 'email' ? 'email' : 'text'}
                    value={form[col.key] ?? ''}
                    onChange={(e) => setForm((f) => ({ ...f, [col.key]: e.target.value }))}
                    placeholder={col.label}
                  />
                </label>
              ))}
            </div>
            <div className="departments-modal-footer">
              <button type="button" className="departments-footer-btn" onClick={() => setShowAddModal(false)}>Cancel</button>
              <button type="button" className="departments-footer-btn departments-footer-btn-primary" onClick={handleSave} disabled={saving || !form.full_name?.trim()}>{saving ? 'Saving…' : 'Save'}</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}